"use client";

import { useCallback, useEffect, useMemo, useState, type ReactNode } from "react";
import { Input } from "@wtc/ui";
import { Search } from "lucide-react";

import type { EmailTemplateFolder, EmailTemplateLike } from "../types";
import { buildFolderTree, rootTemplatesOf } from "../folder-tree";
import { TemplateBrowser } from "./TemplateBrowser";
import { TemplatePreviewPane } from "./TemplatePreviewPane";

/** Debounce a value; the picker searches server-side on the debounced term. */
export function useDebouncedValue<T>(value: T, delayMs: number): { debounced: T; isDebouncing: boolean } {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    if (Object.is(debounced, value)) return;
    const t = setTimeout(() => setDebounced(value), delayMs);
    return () => clearTimeout(t);
    // `debounced` is intentionally read-only here — including it would reset the timer on settle.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value, delayMs]);
  return { debounced, isDebouncing: !Object.is(debounced, value) };
}

export interface TemplatePickerBodyProps<T extends EmailTemplateLike> {
  /** Templates matching the current search/tag — the host owns fetching. */
  templates: T[];
  folders: EmailTemplateFolder[];
  isLoading: boolean;
  /** Current search term (controlled by the host so it can drive its query). */
  searchValue: string;
  onSearchChange: (value: string) => void;
  /** True while the host's debounced term hasn't caught up with `searchValue`. */
  isDebouncing?: boolean;
  /** Term the results actually reflect — used for the empty-state message. */
  appliedSearchTerm?: string;
  selectedTag?: string;
  /** Tag filter control; omit to hide the tag row entirely. */
  tagFilter?: ReactNode;
  favoriteIds: string[];
  isFavorite: (id: string) => boolean;
  onToggleFavorite: (id: string) => void;
  onApplyTemplate: (template: T) => void;
  /** Unfilled `{{variables}}` for the previewed template, if the host resolves a preview. */
  resolveUnfilledVariables?: (template: T) => string[];
  /** Optional row wrapper (Hub passes its context menu). */
  wrapRow?: (template: T, row: ReactNode) => ReactNode;
}

/**
 * The shared body of WTC's ONE email-template picker (EMAIL_TEMPLATES_CANONICAL_SYSTEM):
 * search + tag filter + folder browser + preview pane. Purely presentational — the
 * host supplies data (Hub from `useEmailTemplates`, next-email-client from the
 * workspace-addon REST proxy) and the surrounding container/modal.
 */
export function TemplatePickerBody<T extends EmailTemplateLike>({
  templates,
  folders,
  isLoading,
  searchValue,
  onSearchChange,
  isDebouncing = false,
  appliedSearchTerm,
  selectedTag = "",
  tagFilter,
  favoriteIds,
  isFavorite,
  onToggleFavorite,
  onApplyTemplate,
  resolveUnfilledVariables,
  wrapRow,
}: TemplatePickerBodyProps<T>) {
  const [expandedFolders, setExpandedFolders] = useState<Set<string>>(new Set());
  const [favoritesExpanded, setFavoritesExpanded] = useState(true);
  const [selectedTemplate, setSelectedTemplate] = useState<T | null>(null);

  const rootTemplates = useMemo(() => rootTemplatesOf(templates), [templates]);
  const folderTree = useMemo(() => buildFolderTree(folders, templates), [folders, templates]);
  const favoriteTemplates = useMemo(() => {
    const byId = new Map(templates.map((t) => [t.id, t]));
    const list: T[] = [];
    for (const id of favoriteIds) {
      const template = byId.get(id);
      if (template) list.push(template);
    }
    return list;
  }, [favoriteIds, templates]);

  const searchTerm = appliedSearchTerm ?? searchValue;
  const isSearching = searchTerm.trim().length > 0;

  const toggleFolder = useCallback((folderId: string) => {
    setExpandedFolders((prev) => {
      const next = new Set(prev);
      if (next.has(folderId)) next.delete(folderId);
      else next.add(folderId);
      return next;
    });
  }, []);

  const handleApply = useCallback(
    (template: T) => {
      onApplyTemplate(template);
      setSelectedTemplate(null);
    },
    [onApplyTemplate]
  );

  const unfilled = useMemo(() => {
    if (!selectedTemplate || !resolveUnfilledVariables) return undefined;
    return resolveUnfilledVariables(selectedTemplate);
  }, [selectedTemplate, resolveUnfilledVariables]);

  return (
    <div className="space-y-4 flex-1 flex flex-col overflow-hidden w-full min-w-0">
      <div className="flex flex-col md:flex-row gap-2">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search templates by name, subject, or full template content..."
            value={searchValue}
            onChange={(e) => onSearchChange(e.target.value)}
            className="pl-10"
          />
        </div>
        {tagFilter && <div className="md:w-64">{tagFilter}</div>}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 flex-1 min-h-0">
        <TemplateBrowser
          isLoading={isLoading}
          isDebouncing={isDebouncing}
          rootTemplates={rootTemplates}
          folderTree={folderTree}
          favoriteTemplates={favoriteTemplates}
          favoritesExpanded={favoritesExpanded}
          onFavoritesExpandedChange={setFavoritesExpanded}
          allTemplateCount={templates.length}
          isSearching={isSearching}
          searchTerm={searchTerm.trim()}
          selectedTag={selectedTag}
          selectedTemplateId={selectedTemplate?.id}
          expandedFolders={expandedFolders}
          onToggleFolder={toggleFolder}
          onPreviewTemplate={setSelectedTemplate}
          onApplyTemplate={handleApply}
          isFavorite={isFavorite}
          onToggleFavorite={onToggleFavorite}
          wrapRow={wrapRow}
        />

        <TemplatePreviewPane
          selectedTemplate={selectedTemplate}
          isFavorite={selectedTemplate ? isFavorite(selectedTemplate.id) : false}
          onToggleFavorite={onToggleFavorite}
          onApplyTemplate={handleApply}
          unfilledVariables={unfilled}
        />
      </div>
    </div>
  );
}
